/**
 * useWorkflowSteps — Derive WorkflowConfig from DB WorkflowTransition data.
 *
 * Fetches transitions for a given process key and builds a WorkflowConfig
 * that is compatible with the WorkflowStepper component.
 *
 * Falls back to hardcoded WORKFLOW_REGISTRY if DB returns empty.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { trpcClient } from '@/lib/trpc';
import type { WorkflowConfig } from '@/config/workflow-configs';
import { WORKFLOW_REGISTRY } from '@/config/workflow-configs';
import type { StatusRegistryKey } from '@/config/status-registry';

// ── Process key → StatusRegistryKey mapping ──
const PROCESS_STATUS_MAP: Record<string, StatusRegistryKey> = {
    order: 'OrderStatus',
    quotation: 'QuotationStatus',
    production: 'ProductionOrderStatus',
    production_order: 'ProductionOrderStatus',
    productionOrder: 'ProductionOrderStatus',
    delivery: 'DeliveryStatus',
    purchasing: 'PurchaseOrderStatus',
    purchaseOrder: 'PurchaseOrderStatus',
    outsourcePO: 'OutsourcePOStatus',
    'material-planning': 'MaterialPlanningStatus',
    tooling: 'ToolingStatus',
    warehouse: 'InventoryStatus',
};

// ── Process key → cancelled/rejected/paused statuses ──
const TERMINAL_STATUSES: Record<string, { cancelled?: string; rejected?: string; paused?: string; onHold?: string }> = {
    order: { cancelled: 'CANCELLED' },
    quotation: { rejected: 'REJECTED', cancelled: 'CANCELLED' },
    production: { cancelled: 'CANCELLED', paused: 'PAUSED', onHold: 'ON_HOLD' },
    productionOrder: { cancelled: 'CANCELLED', paused: 'PAUSED', onHold: 'ON_HOLD' },
    outsourcePO: { cancelled: 'CANCELLED' },
    purchaseOrder: { cancelled: 'CANCELLED' },
    delivery: { cancelled: 'CANCELLED' },
};

// ── Process key dùng ở trang → key trong WORKFLOW_REGISTRY / TERMINAL_STATUSES ──
const PROCESS_ALIASES: Record<string, string> = {
    purchasing: 'purchaseOrder',
    production: 'productionOrder',
    production_order: 'productionOrder',
};

export interface UseWorkflowStepsResult {
    workflow: WorkflowConfig;
    isLoading: boolean;
    isFromDB: boolean;
}

export function useWorkflowSteps(processKey: string): UseWorkflowStepsResult {
    const { data: transitionsData, isLoading } = useQuery({
        queryKey: ['workflow-transitions', processKey],
        queryFn: async () => {
            if (!processKey) return [];
            return await trpcClient.config.getTransitionsByProcess.query({ process: processKey });
        },
        enabled: !!processKey,
        staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    });

    const transitions = (transitionsData || []) as any[];

    const workflow = useMemo<WorkflowConfig>(() => {
        const canonicalKey = PROCESS_ALIASES[processKey] || processKey;
        const hardcoded = WORKFLOW_REGISTRY[processKey] || WORKFLOW_REGISTRY[canonicalKey];

        // 1. If DB transitions exist, derive from them
        if (transitions.length > 0) {
            // Derive ordered unique steps from transitions
            // Use topological sort based on fromStatus → toStatus edges
            const fromSet = new Set<string>();
            const toSet = new Set<string>();
            const edges: Array<{ from: string; to: string; sortOrder: number }> = [];

            for (const t of transitions) {
                fromSet.add(t.fromStatus);
                toSet.add(t.toStatus);
                edges.push({ from: t.fromStatus, to: t.toStatus, sortOrder: t.sortOrder || 0 });
            }

            // Find the start status: appears in fromSet but not exclusively in toSet
            const allStatuses = new Set([...fromSet, ...toSet]);

            // Terminal statuses (CANCELLED, REJECTED, PAUSED, ON_HOLD) are excluded from the linear flow dots
            const terminals = TERMINAL_STATUSES[processKey] || TERMINAL_STATUSES[canonicalKey] || {
                cancelled: hardcoded?.cancelledStatus,
                rejected: hardcoded?.rejectedStatus,
                paused: hardcoded?.pausedStatus,
                onHold: hardcoded?.onHoldStatus,
            };
            const branchTerminals = new Set([terminals.cancelled, terminals.rejected, terminals.paused, terminals.onHold].filter(Boolean) as string[]);

            // Build adjacency list (ignoring negative terminals)
            const adj = new Map<string, string[]>();
            const sortedEdges = [...edges].sort((a, b) => a.sortOrder - b.sortOrder);
            for (const e of sortedEdges) {
                if (branchTerminals.has(e.to)) continue; // skip negative terminal transitions
                if (!adj.has(e.from)) adj.set(e.from, []);
                adj.get(e.from)!.push(e.to);
            }

            // A root is a status that only appears in fromStatus, or the minimum sortOrder status
            const nonRootToSet = new Set<string>();
            for (const e of sortedEdges) {
                if (!branchTerminals.has(e.to)) nonRootToSet.add(e.to);
            }
            const roots = [...fromSet].filter(s => !nonRootToSet.has(s) && !branchTerminals.has(s));
            // Prefer the root that has the minimum sortOrder in its outgoing edges
            const sortedRoots = roots.sort((a, b) => {
                const minA = Math.min(...edges.filter(e => e.from === a).map(e => e.sortOrder), 999);
                const minB = Math.min(...edges.filter(e => e.from === b).map(e => e.sortOrder), 999);
                return minA - minB;
            });
            const startStatus = sortedRoots[0] || (sortedEdges.length > 0 ? sortedEdges[0].from : '');

            // Use BFS but prioritize neighbors by their sortOrder to follow the main flow
            const ordered: string[] = [];
            const visited = new Set<string>();
            if (startStatus) {
                const queue = [startStatus];
                visited.add(startStatus);

                while (queue.length > 0) {
                    const current = queue.shift()!;
                    ordered.push(current);
                    
                    const neighbors = adj.get(current) || [];
                    // Neighbors are already sorted by sortOrder because they were pushed from sortedEdges
                    for (const next of neighbors) {
                        if (!visited.has(next)) {
                            visited.add(next);
                            queue.push(next);
                        }
                    }
                }
            }

            // Include any remaining statuses that are NOT negative terminals
            for (const s of allStatuses) {
                if (!visited.has(s) && !branchTerminals.has(s)) {
                    ordered.push(s);
                }
            }

            // Final safety filter: remove any duplicates and ensure no negative terminals
            let finalSteps = Array.from(new Set(ordered)).filter(s => !branchTerminals.has(s));
            // Duyệt theo cạnh chỉ cho thứ tự gần đúng (bước không có cạnh tới bị dồn cuối, VD "Đã duyệt" sau "Đã nhận đủ"):
            // có quy trình chuẩn thì xếp theo thứ tự chuẩn, bước lạ giữ ở cuối (FB-D64YE7)
            if (hardcoded?.steps?.length) {
                const rank = (s: string) => {
                    const i = hardcoded.steps.findIndex(h => h.toUpperCase() === s.toUpperCase());
                    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
                };
                finalSteps = finalSteps
                    .map((s, i) => ({ s, i }))
                    .sort((a, b) => rank(a.s) - rank(b.s) || a.i - b.i)
                    .map(x => x.s);
            }

            // Get label from first transition's processLabel
            const processLabel = transitions[0]?.processLabel || hardcoded?.label || processKey;

            return {
                label: processLabel,
                steps: finalSteps.length > 0 ? finalSteps : (hardcoded?.steps || []),
                cancelledStatus: terminals.cancelled || hardcoded?.cancelledStatus,
                rejectedStatus: terminals.rejected || hardcoded?.rejectedStatus,
                pausedStatus: terminals.paused || hardcoded?.pausedStatus,
                onHoldStatus: terminals.onHold || hardcoded?.onHoldStatus,
                cancelableUntil: hardcoded?.cancelableUntil,
                allowBack: hardcoded?.allowBack ?? true,
                statusMapName: (PROCESS_STATUS_MAP[processKey] || hardcoded?.statusMapName || 'OrderStatus') as StatusRegistryKey,
                stepOrder: hardcoded?.stepOrder || hardcoded?.steps,
                statusAliases: hardcoded?.statusAliases,
            };
        }

        // 2. Fallback to hardcoded registry if DB returns empty
        if (hardcoded) return hardcoded;

        // 3. Ultimate fallback
        return {
            label: processKey,
            steps: [],
            statusMapName: (PROCESS_STATUS_MAP[processKey] || 'OrderStatus') as StatusRegistryKey,
        };
    }, [transitions, processKey]);

    return {
        workflow,
        isLoading,
        isFromDB: transitions.length > 0,
    };
}
